import { useEffect, useRef } from "react";
import { useManualAuthoringStore } from "../../store/manualAuthoringStore";
import BlockRenderer from "./blocks/BlockRenderer";

const toAlphaLabel = (index: number): string => {
  let n = index;
  let out = "";

  while (n > 0) {
    n -= 1;
    out = String.fromCharCode(97 + (n % 26)) + out;
    n = Math.floor(n / 26);
  }

  return out || "a";
};

export default function BlockCanvasVendor() {
  const canvasRef = useRef<HTMLDivElement>(null);
  const manual = useManualAuthoringStore((s) => s.presentManual);
  const manualTitle = useManualAuthoringStore((s) => s.manualTitle);
  const selectedSectionId = useManualAuthoringStore((s) => s.selectedSectionId);
  const selectedSubSectionId = useManualAuthoringStore(
    (s) => s.selectedSubSectionId
  );
  const selectedBlockId = useManualAuthoringStore((s) => s.selectedBlockId);
  const selectBlock = useManualAuthoringStore((s) => s.selectBlock);

  useEffect(() => {
    if (!selectedBlockId) return;

    const rafId = window.requestAnimationFrame(() => {
      const root = canvasRef.current;
      if (!root) return;

      const target = Array.from(
        root.querySelectorAll<HTMLElement>("[data-block-id]")
      ).find((el) => el.dataset.blockId === selectedBlockId);

      if (!target) return;

      const scrollHost = root.closest(
        ".manual-structure-page__main-scroll"
      ) as HTMLElement | null;

      if (!scrollHost) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
        return;
      }

      const hostRect = scrollHost.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const nextTop =
        scrollHost.scrollTop +
        (targetRect.top - hostRect.top) -
        (scrollHost.clientHeight / 2 - targetRect.height / 2);

      scrollHost.scrollTo({
        top: Math.max(0, nextTop),
        behavior: "smooth",
      });
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [selectedBlockId, selectedSectionId, selectedSubSectionId, manual.sections]);

  if (!selectedSectionId) {
    return <div className="pdf-empty-state">Select a section to view content.</div>;
  }

  const section = manual.sections.find((s) => s.id === selectedSectionId);
  if (!section) return null;

  const subSection = selectedSubSectionId
    ? section.subSections?.find((s) => s.id === selectedSubSectionId)
    : null;
  const sectionHeading = subSection
    ? `${section.title} / ${subSection.title}`
    : section.title;
  const manualName = manualTitle?.trim() || "Manual";

  const blocks = selectedSubSectionId
    ? section.subSections?.find((s) => s.id === selectedSubSectionId)?.blocks ??
      []
    : section.blocks ?? [];

  const figureNumberByBlockId: Record<string, number> = {};
  const tableNumberByBlockId: Record<string, number> = {};
  let figureCounter = 0;
  let tableCounter = 0;

  for (const sec of manual.sections) {
    for (const b of sec.blocks || []) {
      if (b.layout === "figure") {
        figureCounter += 1;
        figureNumberByBlockId[b.id] = figureCounter;
      }
      if (b.layout === "table") {
        tableCounter += 1;
        tableNumberByBlockId[b.id] = tableCounter;
      }
    }

    for (const sub of sec.subSections || []) {
      for (const b of sub.blocks || []) {
        if (b.layout === "figure") {
          figureCounter += 1;
          figureNumberByBlockId[b.id] = figureCounter;
        }
        if (b.layout === "table") {
          tableCounter += 1;
          tableNumberByBlockId[b.id] = tableCounter;
        }
      }
    }
  }

  const stepNumberByBlockId: Record<string, string> = {};
  const subStepLabelByBlockId: Record<string, string> = {};
  let stepCounter = 0;
  let subStepCounter = 0;

  for (const b of blocks) {
    if (b.layout === "subheading") {
      stepCounter = 0;
      subStepCounter = 0;
      continue;
    }

    if (b.layout === "step") {
      stepCounter += 1;
      subStepCounter = 0;
      stepNumberByBlockId[b.id] = `${stepCounter}.`;
      continue;
    }

    if (b.layout === "subStep") {
      subStepCounter += 1;
      subStepLabelByBlockId[b.id] = `${toAlphaLabel(subStepCounter)}.`;
    }
  }

  return (
    <div className="pdf-canvas" ref={canvasRef}>
      <div className="editor-header-row">
        <div className="editor-header-meta" />
        <div className="editor-header-paper">
          <div className="editor-header-top">
            <div className="editor-header-section">{sectionHeading}</div>
            <div className="editor-header-manual-wrap">
              <div className="editor-header-manual">{manualName}</div>
              <img
                src="/niquilllogosmall.png"
                alt="Niquill logo"
                className="editor-header-logo"
              />
            </div>
          </div>
          <div className="editor-header-divider" />
        </div>
      </div>

      {blocks.length === 0 && (
        <div className="pdf-empty-state">No blocks in this section.</div>
      )}

      {blocks.map((block) => (
        <div
          key={block.id}
          data-block-id={block.id}
          className="editor-row"
          onClick={() => selectBlock(block.id)}
        >
          <div className="editor-meta" />

          <div className="editor-paper">
            <BlockRenderer
              block={block}
              isEditing={false}
              figureNumber={figureNumberByBlockId[block.id]}
              tableNumber={tableNumberByBlockId[block.id]}
              stepNumber={stepNumberByBlockId[block.id]}
              subStepLabel={subStepLabelByBlockId[block.id]}
              exitEditMode={() => undefined}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
